import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface DepletionMonthlyStudent {
  id: string;
  student_code: string;
  month: number;
  year: number;
  status: string | null;
  estimated_hours_depletion_date: string | null;
  estimated_8hours_date: string | null;
  follow_by: string | null;
  consulted_gift: boolean | null;
  note: string | null;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  hours: number | null;
  salesperson_email: string | null;
  school: string | null;
  crm_status: string | null;
  call_date: string | null;
  consulted_gift_date: string | null;
  latest_call_id: string | null;
  latest_call_date: string | null;
  latest_call_source: string | null;
}

export interface StatusBreakdown {
  status: string;
  count: number;
  percent: number;
}

export interface DepletionMonthlySummary {
  total_depleted: number;
  total_nearly_depleted: number;
  total_hours_depleted: number;
  total_hours_nearly_depleted: number;
  total: number;
  status_breakdown: StatusBreakdown[];
}

export type FilterMode = 'month' | 'dateRange';

interface UseDepletionMonthlyOptions {
  mode: FilterMode;
  month: number;
  year: number;
  startDate?: string;
  endDate?: string;
}

const CACHE_DURATION_MS = 5 * 60 * 1000;

interface CacheEntry {
  students: DepletionMonthlyStudent[];
  fetchedAt: number;
  key: string;
}

export function useDepletionMonthly({ mode, month, year, startDate, endDate }: UseDepletionMonthlyOptions) {
  const [students, setStudents] = useState<DepletionMonthlyStudent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const cacheRef = useRef<CacheEntry | null>(null);
  const fetchIdRef = useRef(0);

  const cacheKey = mode === 'month' ? `m:${month}:${year}` : `d:${startDate}:${endDate}`;

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && cacheRef.current) {
      const cache = cacheRef.current;
      if (!((Date.now() - cache.fetchedAt) > CACHE_DURATION_MS) && cache.key === cacheKey) {
        setStudents(cache.students);
        setLastFetchedAt(new Date(cache.fetchedAt));
        return;
      }
    }

    const currentFetchId = ++fetchIdRef.current;
    setIsLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .schema('crm')
        .from('v_student_depletion_monthly')
        .select('*');

      if (mode === 'month') {
        query = query.eq('month', month).eq('year', year);
      } else {
        // Date range mode: filter by estimated dates in range
        query = query.or(
          `and(estimated_hours_depletion_date.gte.${startDate},estimated_hours_depletion_date.lte.${endDate}),and(estimated_8hours_date.gte.${startDate},estimated_8hours_date.lte.${endDate})`
        );
      }

      query = query.order('estimated_hours_depletion_date', { ascending: true, nullsFirst: false });

      const { data, error } = await query;

      if (error) throw error;
      if (currentFetchId !== fetchIdRef.current) return;

      const result: DepletionMonthlyStudent[] = data ?? [];

      const now = Date.now();
      cacheRef.current = { students: result, fetchedAt: now, key: cacheKey };

      setStudents(result);
      setLastFetchedAt(new Date(now));
    } catch (err) {
      console.error("Error fetching depletion monthly:", err);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถดึงข้อมูลนักเรียนใกล้หมดคอร์สได้",
        variant: "destructive",
      });
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [cacheKey, mode, month, year, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => fetchData(true), [fetchData]);

  const summary = useMemo<DepletionMonthlySummary>(() => {
    const total = students.length;

    let totalDepleted: number;
    let totalNearlyDepleted: number;

    if (mode === 'month') {
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      totalDepleted = students.filter(s =>
        s.estimated_hours_depletion_date &&
        s.estimated_hours_depletion_date >= monthStart &&
        s.estimated_hours_depletion_date <= monthEnd
      ).length;
      totalNearlyDepleted = students.filter(s =>
        s.estimated_8hours_date &&
        s.estimated_8hours_date >= monthStart &&
        s.estimated_8hours_date <= monthEnd
      ).length;
    } else {
      // In date range mode, count those within the date range
      totalDepleted = students.filter(s =>
        s.estimated_hours_depletion_date && startDate && endDate &&
        s.estimated_hours_depletion_date >= startDate && s.estimated_hours_depletion_date <= endDate
      ).length;
      totalNearlyDepleted = students.filter(s =>
        s.estimated_8hours_date && startDate && endDate &&
        s.estimated_8hours_date >= startDate && s.estimated_8hours_date <= endDate
      ).length;
    }

    // Status breakdown
    const statusMap = new Map<string, number>();
    for (const s of students) {
      const key = s.status ?? 'ยังไม่ได้ระบุ';
      statusMap.set(key, (statusMap.get(key) ?? 0) + 1);
    }

    const statusBreakdown: StatusBreakdown[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percent: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    const totalHoursDepleted = students.filter(s => s.hours !== null && s.hours <= 0).length;
    const totalHoursNearlyDepleted = students.filter(s => s.hours !== null && s.hours > 0 && s.hours <= 8).length;

    return { total_depleted: totalDepleted, total_nearly_depleted: totalNearlyDepleted, total_hours_depleted: totalHoursDepleted, total_hours_nearly_depleted: totalHoursNearlyDepleted, total, status_breakdown: statusBreakdown };
  }, [students, mode, startDate, endDate]);

  return { students, summary, isLoading, lastFetchedAt, refresh };
}
